import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  IconButton,
  Modal,
  Typography,
  Icons,
  useTheme,
  Loader,
} from '@ublend-npm/aulaui-core';
import { LTI_CONSUMER } from '@core/constants/endpoints';
import TurnitinLogo from '@static/turnitin.png';

type Assignment = {
  objectId: string;
  title: string;
  description: string;
  endDate: string;
  startDate: string;
  gradesReleasedAt: string;
  maxGrade?: number;
};

type User = {
  email?: string;
  firstName?: string;
  lastName?: string;
};

type Space = {
  objectId: string;
};

type TurnitinModalProps = {
  assignment: Assignment;
  user?: User;
  space: Space;
  onClose: () => void;
  isEducator?: boolean;
};

const FONT_COLOR = '#000000DE';
const TurnitinModal = ({
  assignment,
  user,
  space,
  onClose,
}: TurnitinModalProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeName = useRef(`turnitin-iframe-${Math.random()}`);

  useEffect(() => {
    let active = true;
    const {
      objectId: assignmentId,
      title,
      description,
      endDate: dueDate,
      startDate,
      gradesReleasedAt,
      maxGrade,
    } = assignment;
    const { email = '', firstName = '', lastName = '' } = user ?? {};
    const { objectId: spaceId } = space;

    const params = {
      assignmentId,
      spaceId,
      title,
      description,
      dueDate,
      startDate,
      gradesReleasedAt,
      ...(maxGrade ? { maxGrade } : {}),
      email,
      firstName,
      lastName,
    };

    axios
      .post(LTI_CONSUMER(), params, { withCredentials: true })
      .then((response) => {
        // A missing named iframe would make the browser open a new tab.
        if (!active || !iframeRef.current?.isConnected) return;
        const data = response.data;
        const form = document.createElement('form');
        form.method = data.method;
        form.action = data.url;
        form.target = iframeName.current;

        if (data.body) {
          Object.keys(data.body).forEach((key) => {
            const input = document.createElement('input');
            input.setAttribute('name', key);
            input.setAttribute('value', data.body[key]);
            form.appendChild(input);
          });
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      });

    return () => {
      active = false;
    };
  }, []);

  const isDark = theme.palette.mode === 'dark';

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="turnitin-modal"
      sx={{ alignItems: 'stretch', justifyContent: 'stretch', p: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: {
            xs: 'calc(100% - 0.2rem)',
            md: 'calc(100vw - 3rem)',
          },
          height: {
            xs: '85vh',
            md: 'calc(100vh - 3rem)',
          },
          bgcolor: 'white',
          outline: 'none',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: '1rem', md: '1.5rem' },
            py: '0.75rem',
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src={TurnitinLogo as string}
              alt="Turnitin"
              style={{ width: 32, height: 32 }}
            />
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 500,
                color: FONT_COLOR,
                m: 0,
              }}
            >
              Turnitin: {assignment.title}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close Turnitin"
            onClick={onClose}
            sx={{
              color: isDark ? theme.palette.grey[400] : theme.palette.grey[600],
            }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        {/* Iframe area */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'white',
                zIndex: 1,
              }}
            >
              <Loader />
            </Box>
          )}
          <iframe
            ref={iframeRef}
            title="turnitin"
            name={iframeName.current}
            frameBorder="0"
            onLoad={() => setLoading(false)}
            data-testid="turnitin-iframe"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              border: 'none',
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default TurnitinModal;
